import React, { useEffect, useRef } from "react";
import { TEAL, CARD_TYPES, elementBounds, connectorEndpoints } from "./constants";
import { wrapText, LINE_HEIGHT } from "./textLayout";
import ImageSlotEl from "./ImageSlotEl";
import ChartEl from "./ChartEl";

export function StrokeEl({ el }) {
  if (!el.points || el.points.length < 2) return null;
  const d = el.points.reduce(
    (acc, p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `${acc} L ${p.x} ${p.y}`),
    ""
  );
  return (
    <path
      d={d}
      stroke={el.color}
      strokeWidth={el.size}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity={el.tool === "highlighter" ? 0.35 : 1}
    />
  );
}

export function ShapeEl({ el }) {
  const { type, x, y, w, h, color, size, dash } = el;
  const s = {
    stroke: color,
    strokeWidth: size,
    fill: "none",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    ...(dash ? { strokeDasharray: dash } : {}),
  };
  if (type === "rect")     return <rect x={x} y={y} width={w} height={h} rx={2} {...s} />;
  if (type === "circle")   return <ellipse cx={x + w / 2} cy={y + h / 2} rx={Math.abs(w / 2)} ry={Math.abs(h / 2)} {...s} />;
  if (type === "triangle") return <polygon points={`${x + w / 2},${y} ${x + w},${y + h} ${x},${y + h}`} {...s} />;
  if (type === "line")     return <line x1={x} y1={y} x2={x + w} y2={y + h} {...s} />;
  if (type === "arrow") {
    const ang = Math.atan2(h, w);
    const len = 20;
    return (
      <g stroke={color} strokeWidth={size} strokeLinecap="round" fill="none">
        <line x1={x} y1={y} x2={x + w} y2={y + h} />
        <line x1={x + w} y1={y + h} x2={x + w - len * Math.cos(ang - 0.4)} y2={y + h - len * Math.sin(ang - 0.4)} />
        <line x1={x + w} y1={y + h} x2={x + w - len * Math.cos(ang + 0.4)} y2={y + h - len * Math.sin(ang + 0.4)} />
      </g>
    );
  }
  return null;
}

function EditableBody({ el, isEditing, onEdit, onEditDone, className, style, placeholder }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!isEditing || !ref.current) return undefined;
    const node = ref.current;
    const frame = requestAnimationFrame(() => {
      node.focus();
      const range = document.createRange();
      range.selectNodeContents(node);
      const sel = window.getSelection();
      if (!sel) return;
      sel.removeAllRanges();
      sel.addRange(range);
    });
    return () => cancelAnimationFrame(frame);
  }, [isEditing]);

  return (
    <div
      xmlns="http://www.w3.org/1999/xhtml"
      ref={ref}
      contentEditable={isEditing}
      suppressContentEditableWarning
      onDoubleClick={() => onEdit(el.id)}
      onBlur={(e) => isEditing && onEditDone(el.id, e.currentTarget.textContent)}
      onKeyDown={(e) => {
        if (e.key === "Escape" || (e.key === "Enter" && !e.shiftKey)) {
          e.preventDefault();
          onEditDone(el.id, e.currentTarget.textContent);
        }
      }}
      className={className}
      style={style}
      data-placeholder={isEditing ? placeholder : undefined}
    >
      {el.content}
    </div>
  );
}

function StaticText({ x, y, width, fontSize, color, content, weight = 600 }) {
  const lines = wrapText(content, width, fontSize);
  return (
    <text
      x={x}
      y={y + fontSize}
      fontSize={fontSize}
      fontWeight={weight}
      fill={color}
      fontFamily="'DM Sans', sans-serif"
      style={{ userSelect: "none" }}
    >
      {lines.map((line, i) => (
        <tspan key={i} x={x} dy={i === 0 ? 0 : fontSize * LINE_HEIGHT}>
          {line || " "}
        </tspan>
      ))}
    </text>
  );
}

export function TextEl({ el, onEdit, isEditing, onEditDone }) {
  const fontSize = Math.max(12, el.size * 5);
  const width = Math.max(el.w || 200, 80);
  const height = Math.max(el.h || 0, Math.ceil(fontSize * 1.35) + 10);

  if (!isEditing) {
    return (
      <StaticText
        x={el.x} y={el.y} width={width}
        fontSize={fontSize} color={el.color} content={el.content}
      />
    );
  }

  return (
    <foreignObject
      x={el.x} y={el.y}
      width={Math.max(el.w || 200, 80)} height={height}
      style={{ overflow: "visible" }}
    >
      <EditableBody
        el={el}
        isEditing={isEditing}
        onEdit={onEdit}
        onEditDone={onEditDone}
        placeholder="Type…"
        className={`font-['DM_Sans'] font-semibold leading-[1.35] whitespace-pre-wrap break-words rounded min-w-[40px] min-h-[20px] ${
          isEditing
            ? "select-text outline-2 outline-[#4DB8B5] outline p-[2px_4px] bg-white/95 cursor-text"
            : "select-none outline-none p-0 bg-transparent cursor-inherit"
        }`}
        style={{ fontSize, color: el.color }}
      />
    </foreignObject>
  );
}

export function StickyEl({ el, onEdit, isEditing, onEditDone }) {
  const w = el.w || 130;
  const h = el.h || 110;
  const rotate = el.rotate ?? 0;
  return (
    <g transform={`rotate(${rotate} ${el.x + w / 2} ${el.y + h / 2})`}>
      <rect
        x={el.x} y={el.y} width={w} height={h} rx={2}
        fill={el.bg || "#FEF08A"}
        stroke="rgba(0,0,0,0.08)"
        strokeWidth={1}
        style={{ filter: "drop-shadow(0 3px 5px rgba(0,0,0,0.18))" }}
      />
      {!isEditing && (
        <StaticText
          x={el.x + 8} y={el.y + 8} width={w - 16}
          fontSize={Math.max(11, (el.size || 2.4) * 5)}
          color={el.color || "#1E293B"} content={el.content} weight={500}
        />
      )}
      {isEditing && (
      <foreignObject x={el.x + 8} y={el.y + 8} width={w - 16} height={h - 16}>
        <EditableBody
          el={el}
          isEditing={isEditing}
          onEdit={onEdit}
          onEditDone={onEditDone}
          placeholder="Type…"
          className={`font-['DM_Sans'] leading-[1.3] whitespace-pre-wrap break-words h-full ${
            isEditing ? "select-text outline-none cursor-text" : "select-none outline-none cursor-inherit"
          }`}
          style={{ fontSize: Math.max(11, (el.size || 2.4) * 5), color: el.color || "#1E293B" }}
        />
      </foreignObject>
      )}
    </g>
  );
}

export function GlyphEl({ el }) {
  const w = el.w || 60;
  const h = el.h || 60;
  return (
    <text
      x={el.x + w / 2}
      y={el.y + h / 2}
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={Math.min(w, h) * 0.8}
      style={{ userSelect: "none" }}
      fill={el.color || "#1E293B"}
    >
      {el.glyph}
    </text>
  );
}

export function TapeEl({ el }) {
  const w = el.w || 160;
  const h = el.h || 42;
  const rotate = el.rotate ?? -4;
  const notch = 8;
  const points = [
    `${el.x},${el.y}`,
    `${el.x + w - notch},${el.y + 3}`,
    `${el.x + w},${el.y + h / 2}`,
    `${el.x + w - notch},${el.y + h - 3}`,
    `${el.x},${el.y + h}`,
    `${el.x + notch},${el.y + h / 2}`,
  ].join(" ");
  return (
    <g transform={`rotate(${rotate} ${el.x + w / 2} ${el.y + h / 2})`}>
      <polygon points={points} fill={el.bg || "#4DB8B5"} opacity={0.45} />
      <polygon points={points} fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth={1} />
    </g>
  );
}

export function FrameEl({ el }) {
  const w = el.w || 220;
  const h = el.h || 160;
  const mat = 10;
  return (
    <g>
      <rect x={el.x} y={el.y} width={w} height={h} rx={3}
        fill="rgba(255,255,255,0.55)" stroke={el.color || "#94A3B8"} strokeWidth={el.size || 3} />
      <rect x={el.x + mat} y={el.y + mat} width={Math.max(w - mat * 2, 0)} height={Math.max(h - mat * 2, 0)}
        fill="none" stroke={el.color || "#94A3B8"} strokeWidth={1} opacity={0.6} />
    </g>
  );
}

export function ArtboardEl({ el }) {
  const w = el.w || 200;
  const h = el.h || 150;
  return (
    <g>
      <rect x={el.x} y={el.y} width={w} height={h} rx={6}
        fill="rgba(77,184,181,0.035)" stroke={TEAL} strokeWidth={1.5}
        strokeDasharray="9 6" />
      <text x={el.x + 4} y={el.y - 6} fontSize={11} fontWeight={700} fill={TEAL}
        fontFamily="'DM Sans', sans-serif" style={{ userSelect: "none" }}>
        {el.name || "Artboard"}
      </text>
    </g>
  );
}

export function ConnectorEl({ el, byId }) {
  const ends = connectorEndpoints(el, byId);
  if (!ends) return null;

  const { a, b } = ends;
  const stroke = el.color || "#475569";
  const width = el.size || 2;

  if (!el.arrow) {
    return <line x1={a.x} y1={a.y} x2={b.x} y2={b.y}
      stroke={stroke} strokeWidth={width} strokeLinecap="round" />;
  }

  const ang = Math.atan2(b.y - a.y, b.x - a.x);
  const head = 12;
  return (
    <g stroke={stroke} strokeWidth={width} strokeLinecap="round" fill="none">
      <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} />
      <line x1={b.x} y1={b.y} x2={b.x - head * Math.cos(ang - 0.4)} y2={b.y - head * Math.sin(ang - 0.4)} />
      <line x1={b.x} y1={b.y} x2={b.x - head * Math.cos(ang + 0.4)} y2={b.y - head * Math.sin(ang + 0.4)} />
    </g>
  );
}

function Attribution({ el }) {
  if (!el.attribution?.photographer) return null;
  return (
    <text x={el.x} y={el.y + el.h + 11} fontSize={9} fill="#94A3B8"
      fontFamily="'DM Sans', sans-serif" style={{ userSelect: "none" }}>
      {`${el.attribution.photographer} on ${el.attribution.source || "Unsplash"}`}
    </text>
  );
}

export function ImageEl({ el }) {
  if (!el.src) {
    return (
      <g>
        <rect x={el.x} y={el.y} width={el.w} height={el.h} rx={4}
          fill="rgba(0,0,0,0.04)" stroke="rgba(0,0,0,0.12)" strokeDasharray="6 4" />
        <text x={el.x + el.w / 2} y={el.y + el.h / 2} textAnchor="middle" dominantBaseline="central"
          fontSize={12} fill="#94A3B8" className="font-['DM_Sans']">
          Image unavailable
        </text>
      </g>
    );
  }

  const crop = el.crop;
  if (!crop) {
    return (
      <g>
        <image href={el.src} x={el.x} y={el.y} width={el.w} height={el.h}
          preserveAspectRatio="xMidYMid meet" style={{ borderRadius: 6 }} />
        <Attribution el={el} />
      </g>
    );
  }

  const { sx = 0, sy = 0, sw = 1, sh = 1 } = crop;
  const fullW = el.w / (sw || 1);
  const fullH = el.h / (sh || 1);
  const clipId = `crop-${el.id}`;
  return (
    <g>
      <defs>
        <clipPath id={clipId}>
          <rect x={el.x} y={el.y} width={el.w} height={el.h} rx={4} />
        </clipPath>
      </defs>
      <image
        href={el.src}
        x={el.x - sx * fullW}
        y={el.y - sy * fullH}
        width={fullW}
        height={fullH}
        preserveAspectRatio="none"
        clipPath={`url(#${clipId})`}
      />
      <Attribution el={el} />
    </g>
  );
}

export function LumenCardEl({ el }) {
  const ct = CARD_TYPES[el.cardType] || CARD_TYPES.idea;
  const w = el.w || 200;
  const h = el.h || 110;
  return (
    <g>
      <rect x={el.x} y={el.y} width={w} height={h} rx={10} ry={10}
        fill="#FFFFFF" stroke={ct.border} strokeWidth={1.5} />
      <rect x={el.x} y={el.y} width={w} height={h} rx={10} ry={10} fill={ct.bg} />
      <text x={el.x + 10} y={el.y + 17} fontSize={9} fontWeight={700} fill={ct.color}
        letterSpacing="0.05em" className="font-['DM_Sans']" style={{ userSelect: "none" }}>
        {`${ct.icon} ${ct.label}`.toUpperCase()}
      </text>
      <StaticText x={el.x + 10} y={el.y + 24} width={w - 20} fontSize={12}
        color="#1E293B" content={el.content || ""} weight={400} />
    </g>
  );
}

export const HANDLE_CURSORS = [
  "nw-resize", "n-resize", "ne-resize", "e-resize",
  "se-resize", "s-resize", "sw-resize", "w-resize",
];

export function SelectionHandles({ el, onHandleDown }) {
  if (!el || el.type === "stroke") return null;
  const bounds = elementBounds(el);
  if (!bounds) return null;
  const { x, y, w, h } = bounds;
  const pts = [
    [x, y], [x + w / 2, y], [x + w, y], [x + w, y + h / 2],
    [x + w, y + h], [x + w / 2, y + h], [x, y + h], [x, y + h / 2],
  ];
  return (
    <g className="pointer-events-auto">
      <rect x={x - 1} y={y - 1} width={w + 2} height={h + 2}
        fill="none" stroke={TEAL} strokeWidth={1.5} strokeDasharray="5 3"
        className="pointer-events-none" />
      {pts.map(([cx, cy], i) => (
        <rect key={i} x={cx - 5} y={cy - 5} width={10} height={10} rx={2}
          fill="white" stroke={TEAL} strokeWidth={1.5}
          className="pointer-events-auto"
          style={{ cursor: HANDLE_CURSORS[i] }}
          onMouseDown={(e) => { e.stopPropagation(); onHandleDown(e, i); }} />
      ))}
    </g>
  );
}

export function BoardElement({ el, editingTextId, onEdit, onEditDone, byId }) {
  const isEditing = editingTextId === el.id;
  switch (el.type) {
    case "stroke":     return <StrokeEl el={el} />;
    case "rect":
    case "circle":
    case "triangle":
    case "line":
    case "arrow":      return <ShapeEl el={el} />;
    case "text":       return <TextEl el={el} isEditing={isEditing} onEdit={onEdit} onEditDone={onEditDone} />;
    case "sticky":     return <StickyEl el={el} isEditing={isEditing} onEdit={onEdit} onEditDone={onEditDone} />;
    case "sticker":
    case "emoji":      return <GlyphEl el={el} />;
    case "tape":       return <TapeEl el={el} />;
    case "frame":      return <FrameEl el={el} />;
    case "image":      return <ImageEl el={el} />;
    case "chart":      return <ChartEl el={el} />;
    case "artboard":   return <ArtboardEl el={el} />;
    case "connector":  return <ConnectorEl el={el} byId={byId} />;
    case "image_slot": return <ImageSlotEl el={el} />;
    case "lumen_card": return <LumenCardEl el={el} />;
    default:           return null;
  }
}
